import React, { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import { surface, tone } from '../timeclock/tcFormat';
import { hours, kindColor, kindLabel, shortDate, specialLabel, teamError, teamPost } from './teamFormat';

const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Import a personal Excel leave sheet ("Blad1": months in columns F..Q,
 * days in rows 2..32, colours for leave/sick/replacement/special, negative
 * numbers for recup). First a check next to the sheet's own totals, then the
 * import, which replaces an earlier import of that person and year.
 */
const TeamImportTab = ({ t, lang, theme, token, staff, year, notify, onImported }) => {
    const [staffId, setStaffId] = useState('');
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const input = useRef(null);

    const reset = () => { setPreview(null); setError(''); };

    const pickFile = (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        reset();
        if (!f) return;
        if (f.size > MAX_BYTES) {
            setError(t('team_err_file', 'This file is not a leave sheet that can be read (.xlsx).'));
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const text = String(reader.result || '');
            setFile({ name: f.name, base64: text.substring(text.indexOf(',') + 1) });
        };
        reader.readAsDataURL(f);
    };

    const run = (action) => {
        setBusy(true);
        setError('');
        teamPost(action, token, { staff_id: staffId, year, file: file.base64 }).then((response) => {
            setBusy(false);
            if (!response?.success) {
                setError(teamError(t, response));
                return;
            }
            if (action === 'importPreview') {
                setPreview(response.data);
            } else {
                notify(t('team_import_done', 'Imported') + ': ' + response.data.requests + ' ' + t('team_import_periods', 'periods'), true);
                setPreview(null);
                setFile(null);
                onImported();
            }
        });
    };

    const s = surface(theme);
    const person = staff.find((p) => p.id === staffId);
    const compareLabel = {
        leave_planned: t('team_cmp_leave', 'Leave planned (A31)'),
        recup_year: t('team_cmp_recup', 'Recup at year end (R33)'),
        entitlement: t('team_cmp_right', 'Leave right (A32)'),
    };

    return (
        <Box sx={{ maxWidth: 900 }}>
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2, maxWidth: 700 }}>
                {t('team_import_intro', 'Read a personal leave sheet (Excel) into the planner, to test with or to switch over. An earlier import of the same colleague and year is replaced; days already entered in the planner stay.')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                <TextField select size="small" label={t('team_col_name', 'Colleague')} value={staffId}
                    onChange={(e) => { setStaffId(e.target.value); reset(); }} sx={{ width: 220 }}>
                    {staff.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </TextField>
                <input ref={input} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style={{ display: 'none' }} onChange={pickFile} />
                <Button variant="outlined" startIcon={<UploadFileOutlined />} onClick={() => input.current?.click()} sx={{ textTransform: 'none', height: 40 }}>
                    {file ? file.name : t('team_pick_file', 'Choose Excel file')}
                </Button>
                <Button variant="contained" disableElevation disabled={!staffId || !file || busy} onClick={() => run('importPreview')}
                    sx={{ textTransform: 'none', height: 40 }}>
                    {busy && !preview ? t('tc_busy', 'One moment…') : t('team_import_check', 'Check')} · {year}
                </Button>
            </Box>

            {error && <Typography sx={{ color: tone.bad, fontSize: 13.5, mb: 1.5 }}>{error}</Typography>}

            {preview && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ ...s.card, p: 2 }}>
                        <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 1 }}>{t('team_import_compare', 'Sheet totals next to the planner')}</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) auto auto auto', columnGap: 3, rowGap: 0.75, fontSize: 13.5, alignItems: 'center' }}>
                            <Box sx={{ color: s.faint, fontSize: 12, fontWeight: 700 }} />
                            <Box sx={{ color: s.faint, fontSize: 12, fontWeight: 700, textAlign: 'right' }}>{t('team_sheet', 'Sheet')}</Box>
                            <Box sx={{ color: s.faint, fontSize: 12, fontWeight: 700, textAlign: 'right' }}>{t('team_planner', 'Planner')}</Box>
                            <Box />
                            {preview.compare.map((c) => (
                                <React.Fragment key={c.key}>
                                    <Box>{compareLabel[c.key]}</Box>
                                    <Box sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{hours(c.sheet, lang)}</Box>
                                    <Box sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.planner === null ? '—' : hours(c.planner, lang)}</Box>
                                    <Box sx={{ color: c.match ? tone.ok : tone.warn, fontWeight: 600 }}>
                                        {c.match ? '✓ ' + t('team_match', 'same') : c.planner === null ? t('team_import_right_new', 'set from the sheet') : '≠ ' + t('team_differs', 'differs')}
                                    </Box>
                                </React.Fragment>
                            ))}
                        </Box>
                        {preview.warnings.length > 0 && (
                            <Box sx={{ mt: 1.5, fontSize: 13.5, color: tone.warn }}>
                                {preview.warnings.map((w) => (
                                    <div key={w.day}>
                                        {shortDate(w.day)}: {t('team_import_grey_workday', 'grey like a holiday, but a working day by roster. Imported as worked; check the sheet.')}
                                    </div>
                                ))}
                            </Box>
                        )}
                        {preview.skipped_existing.length > 0 && (
                            <Typography sx={{ mt: 1, fontSize: 13.5, color: 'text.secondary' }}>
                                {t('team_import_skipped', 'Already in the planner, left as is')}: {preview.skipped_existing.map(shortDate).join(', ')}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ ...s.card, p: 2 }}>
                        <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 1 }}>
                            {preview.requests.length} {t('team_import_periods', 'periods')} · {person?.name}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr auto', columnGap: 2, rowGap: 0.6, fontSize: 13.5, alignItems: 'center' }}>
                            {preview.requests.map((r, i) => (
                                <React.Fragment key={i}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: kindColor(r.kind, theme), flexShrink: 0 }} />
                                        {kindLabel(t, r.kind)}
                                    </Box>
                                    <Box sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                        {shortDate(r.from)}{r.to !== r.from ? ' – ' + shortDate(r.to) : ''}
                                    </Box>
                                    <Box sx={{ color: 'text.secondary', minWidth: 0 }}>
                                        {r.special_code ? specialLabel(t, r.special_code) + (r.remark ? ' (' + r.remark.replace(/^Excel: /, '') + ')' : '') : ''}
                                        {r.replaces_holiday ? t('team_replaces', 'for holiday') + ' ' + shortDate(r.replaces_holiday) : ''}
                                    </Box>
                                    <Box sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                        {r.days} {r.days === 1 ? t('team_day', 'day') : t('team_days', 'days')} · {hours(r.hours, lang)}
                                    </Box>
                                </React.Fragment>
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <Button variant="contained" disableElevation disabled={busy} onClick={() => run('importCommit')} sx={{ textTransform: 'none', height: 40 }}>
                            {busy ? t('tc_busy', 'One moment…') : t('team_import_go', 'Import into the planner')}
                        </Button>
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                            {t('team_import_go_hint', 'Approved at once; replaces an earlier import of this colleague for')} {year}.
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default TeamImportTab;
